import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider } from "./contexts/LanguageContext";
import "@/App.css";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import RoleSelection from "./pages/RoleSelection";
import JobSeekerDashboard from "./pages/JobSeekerDashboard";
import RecruiterDashboard from "./pages/RecruiterDashboard";
import ProfileBuilder from "./pages/ProfileBuilder";
import JobCreator from "./pages/JobCreator";
import JobDetails from "./pages/JobDetails";
import AIInterviewer from "./pages/AIInterviewer";
import Community from "./pages/Community";
import Subscription from "./pages/Subscription";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionCallback from "./pages/SubscriptionCallback";
import CareerInsights from "./pages/CareerInsights";
import CandidateSearch from "./pages/CandidateSearch";
import Addons from "./pages/Addons";
import AddonsSuccess from "./pages/AddonsSuccess";
import JobsBrowser from "./pages/JobsBrowser";
import Messages from "./pages/Messages";
import RecruiterAnalytics from "./pages/RecruiterAnalytics";
import JobSeekerAnalytics from "./pages/JobSeekerAnalytics";
import EditJob from "./pages/EditJob";
import CandidateComparison from "./pages/CandidateComparison";
import SkillGapAnalysis from "./pages/SkillGapAnalysis";
import Events from "./pages/Events";
import CandidateProfile from "./pages/CandidateProfile";
import RecruiterProfile from "./pages/RecruiterProfile";
import InterviewInvitations from "./pages/InterviewInvitations";
import InterviewResults from "./pages/InterviewResults";
import CalendarIntegration from "./pages/CalendarIntegration";
import ExternalJobs from "./pages/ExternalJobs";
import SavedJobs from "./pages/SavedJobs";
import JobAlerts from "./pages/JobAlerts";
import NotificationSettings from "./pages/NotificationSettings";
import AllNotifications from "./pages/AllNotifications";
import AdvancedAnalytics from "./pages/AdvancedAnalytics";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLogin from "./pages/AdminLogin";
import AdminActivity from "./pages/AdminActivity";
import AdminModeration from "./pages/AdminModeration";
import AdminBulkManagement from "./pages/AdminBulkManagement";
import AdminExport from "./pages/AdminExport";
import AdminSettings from "./pages/AdminSettings";
import AdminUsers from "./pages/AdminUsers";
import AdminJobs from "./pages/AdminJobs";
import AdminNotifications from "./pages/AdminNotifications";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import AdminRevenue from "./pages/AdminRevenue";
import AdminGrowth from "./pages/AdminGrowth";
import RecruiterCRM from "./pages/RecruiterCRM";
import MarketInsights from "./pages/MarketInsights";
import ForYou from "./pages/ForYou";
import VideoCall from "./pages/VideoCall";
import BulkMessaging from "./pages/BulkMessaging";
import SettingsPage from "./pages/SettingsPage";
import JobSeekerMarketInsights from "./pages/JobSeekerMarketInsights";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminExperiments from "./pages/AdminExperiments";
import AdminEmailAnalytics from "./pages/AdminEmailAnalytics";
import AmbassadorDashboard from "./pages/AmbassadorDashboard";
import AdminAmbassadors from "./pages/AdminAmbassadors";
import NotificationsPage from "./pages/NotificationsPage";
import ShortlistDashboard from "./pages/ShortlistDashboard";
import BadgesPage from "./pages/BadgesPage";
import ManageJobs from "./pages/ManageJobs";
import ReferralPage from "./pages/ReferralPage";
import RewardsCatalog from "./pages/RewardsCatalog";
import ResumeOptimization from "./pages/ResumeOptimization";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Support from "./pages/Support";
import About from "./pages/About";
import NewsletterPreferences from "./pages/NewsletterPreferences";
import ResetPassword from "./pages/ResetPassword";
import ApplicantPipeline from "./pages/ApplicantPipeline";
import UserSearch from "./pages/UserSearch";
import UserProfile from "./pages/UserProfile";
import MyApplications from "./pages/MyApplications";
import InterviewRecordings from "./pages/InterviewRecordings";
import AssessmentsBrowser from "./pages/AssessmentsBrowser";
import TakeAssessment from "./pages/TakeAssessment";
import AssessmentCreator from "./pages/AssessmentCreator";
import CVBuilder from "./pages/CVBuilder";
import CompanyProfile from "./pages/CompanyProfile";
import CompanyDirectory from "./pages/CompanyDirectory";
import InterviewPrep from "./pages/InterviewPrep";
import SalaryResearch from "./pages/SalaryResearch";
import ApplicationBoard from "./pages/ApplicationBoard";
import TrendingJobs from "./pages/TrendingJobs";

// PWA Components
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import OfflineIndicator from "./components/OfflineIndicator";

// Use relative URLs for production (when on rekrutor.com), absolute for development
const isProduction = typeof window !== 'undefined' && (
  window.location.hostname === 'rekrutor.com' || 
  window.location.hostname === 'www.rekrutor.com' ||
  window.location.hostname.endsWith('.emergent.host')
);
export const API = isProduction ? '/api' : `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const originalTitle = useRef("Rekrutor");
  const pollingRef = useRef(null);

  const checkAuth = async () => {
    try {
      // Try to get session token from localStorage as fallback
      const storedToken = localStorage.getItem("session_token");
      const headers = storedToken ? { "Authorization": `Bearer ${storedToken}` } : {};
      
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        // Clear invalid token
        localStorage.removeItem("session_token");
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread message count and update browser tab
  const fetchUnreadCount = async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API}/messages/unread/count`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        const count = data.count || 0;
        setUnreadCount(count);
        
        // Update browser tab title
        if (count > 0) {
          document.title = `(${count}) Rekrutor`;
        } else {
          document.title = "Rekrutor";
        }
      }
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setUnreadCount(0);
    document.title = "Rekrutor";
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Start polling for unread messages when user is authenticated
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Poll every 30 seconds
      pollingRef.current = setInterval(fetchUnreadCount, 30000);
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, unreadCount, fetchUnreadCount }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/google" element={<GoogleAuthCallback />} />
      <Route
        path="/role-selection"
        element={
          <ProtectedRoute>
            <RoleSelection />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/jobseeker"
        element={
          <ProtectedRoute>
            <JobSeekerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/recruiter"
        element={
          <ProtectedRoute>
            <RecruiterDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/build"
        element={
          <ProtectedRoute>
            <ProfileBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/create"
        element={
          <ProtectedRoute>
            <JobCreator />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/:jobId"
        element={
          <JobDetails />
        }
      />
      <Route
        path="/interview/:sessionId"
        element={
          <ProtectedRoute>
            <AIInterviewer />
          </ProtectedRoute>
        }
      />
      <Route path="/community" element={<Community />} />
      <Route path="/subscription" element={<Subscription />} />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <SubscriptionSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/callback"
        element={
          <ProtectedRoute>
            <SubscriptionCallback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/career-insights"
        element={
          <ProtectedRoute>
            <CareerInsights />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resume-optimization"
        element={
          <ProtectedRoute>
            <ResumeOptimization />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resume-optimization/:jobId"
        element={
          <ProtectedRoute>
            <ResumeOptimization />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidates"
        element={
          <ProtectedRoute>
            <CandidateSearch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/applicant-pipeline"
        element={
          <ProtectedRoute>
            <ApplicantPipeline />
          </ProtectedRoute>
        }
      />
      <Route
        path="/addons"
        element={
          <ProtectedRoute>
            <Addons />
          </ProtectedRoute>
        }
      />
      <Route
        path="/addons/success"
        element={
          <ProtectedRoute>
            <AddonsSuccess />
          </ProtectedRoute>
        }
      />
      <Route path="/jobs" element={<JobsBrowser />} />
      <Route
        path="/jobs/saved"
        element={
          <ProtectedRoute>
            <SavedJobs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/alerts"
        element={
          <ProtectedRoute>
            <JobAlerts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/recruiter"
        element={
          <ProtectedRoute>
            <RecruiterAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm"
        element={
          <ProtectedRoute>
            <RecruiterCRM />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/jobseeker"
        element={
          <ProtectedRoute>
            <JobSeekerAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/:jobId/edit"
        element={
          <ProtectedRoute>
            <EditJob />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidates/compare"
        element={
          <ProtectedRoute>
            <CandidateComparison />
          </ProtectedRoute>
        }
      />
      <Route
        path="/skills/analysis"
        element={
          <ProtectedRoute>
            <SkillGapAnalysis />
          </ProtectedRoute>
        }
      />
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <Events />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidate/:candidateId"
        element={
          <ProtectedRoute>
            <CandidateProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidates/:candidateId"
        element={
          <ProtectedRoute>
            <CandidateProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/recruiter"
        element={
          <ProtectedRoute>
            <RecruiterProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviews"
        element={
          <ProtectedRoute>
            <InterviewInvitations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviews/:sessionId"
        element={
          <ProtectedRoute>
            <InterviewResults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarIntegration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/external-jobs"
        element={
          <ProtectedRoute>
            <ExternalJobs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/settings"
        element={
          <ProtectedRoute>
            <NotificationSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/all"
        element={
          <ProtectedRoute>
            <AllNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/advanced"
        element={
          <ProtectedRoute>
            <AdvancedAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/market-insights"
        element={
          <ProtectedRoute>
            <MarketInsights />
          </ProtectedRoute>
        }
      />
      <Route
        path="/for-you"
        element={
          <ProtectedRoute>
            <ForYou />
          </ProtectedRoute>
        }
      />
      <Route
        path="/video-call/:interviewId"
        element={
          <ProtectedRoute>
            <VideoCall />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bulk-messaging"
        element={
          <ProtectedRoute>
            <BulkMessaging />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobseeker/market-insights"
        element={
          <ProtectedRoute>
            <JobSeekerMarketInsights />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute>
            <AdminAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/experiments"
        element={
          <ProtectedRoute>
            <AdminExperiments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/email-analytics"
        element={
          <ProtectedRoute>
            <AdminEmailAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/activity"
        element={
          <ProtectedRoute>
            <AdminActivity />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/moderation"
        element={
          <ProtectedRoute>
            <AdminModeration />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/bulk"
        element={
          <ProtectedRoute>
            <AdminBulkManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/export"
        element={
          <ProtectedRoute>
            <AdminExport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute>
            <AdminSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/jobs"
        element={
          <ProtectedRoute>
            <AdminJobs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute>
            <AdminNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/subscriptions"
        element={
          <ProtectedRoute>
            <AdminSubscriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/revenue"
        element={
          <ProtectedRoute>
            <AdminRevenue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/growth"
        element={
          <ProtectedRoute>
            <AdminGrowth />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ambassadors"
        element={
          <ProtectedRoute>
            <AdminAmbassadors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ambassador"
        element={
          <ProtectedRoute>
            <AmbassadorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shortlist/:jobId"
        element={
          <ProtectedRoute>
            <ShortlistDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/badges"
        element={
          <ProtectedRoute>
            <BadgesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search/users"
        element={
          <ProtectedRoute>
            <UserSearch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:userId"
        element={
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-applications"
        element={
          <ProtectedRoute>
            <MyApplications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recordings"
        element={
          <ProtectedRoute>
            <InterviewRecordings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs/manage"
        element={
          <ProtectedRoute>
            <ManageJobs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral"
        element={
          <ProtectedRoute>
            <ReferralPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards"
        element={
          <ProtectedRoute>
            <RewardsCatalog />
          </ProtectedRoute>
        }
      />
      {/* Assessments */}
      <Route
        path="/assessments"
        element={
          <ProtectedRoute>
            <AssessmentsBrowser />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessments/create"
        element={
          <ProtectedRoute>
            <AssessmentCreator />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessments/:assessmentId"
        element={
          <ProtectedRoute>
            <TakeAssessment />
          </ProtectedRoute>
        }
      />
      {/* CV Builder */}
      <Route
        path="/cv-builder"
        element={
          <ProtectedRoute>
            <CVBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cv-builder/:jobId"
        element={
          <ProtectedRoute>
            <CVBuilder />
          </ProtectedRoute>
        }
      />
      {/* Company Pages */}
      <Route
        path="/companies"
        element={
          <ProtectedRoute>
            <CompanyDirectory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/company/:companyName"
        element={
          <ProtectedRoute>
            <CompanyProfile />
          </ProtectedRoute>
        }
      />
      {/* Interview Prep */}
      <Route
        path="/interview-prep"
        element={
          <ProtectedRoute>
            <InterviewPrep />
          </ProtectedRoute>
        }
      />
      {/* Salary Research */}
      <Route
        path="/salary-research"
        element={
          <ProtectedRoute>
            <SalaryResearch />
          </ProtectedRoute>
        }
      />
      {/* Application Board */}
      <Route
        path="/applications"
        element={
          <ProtectedRoute>
            <ApplicationBoard />
          </ProtectedRoute>
        }
      />
      {/* Trending */}
      <Route
        path="/trending"
        element={
          <ProtectedRoute>
            <TrendingJobs />
          </ProtectedRoute>
        }
      />
      {/* Public Pages */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/support" element={<Support />} />
      <Route path="/about" element={<About />} />
      <Route path="/newsletter/preferences" element={<NewsletterPreferences />} />
      <Route path="/reset-password" element={<ResetPassword />} />
    </Routes>
  );
}

import { PaywallProvider } from "./components/PaywallModal";
import { CommandPaletteProvider } from "./components/CommandPalette";
import { CoPilotProvider } from "./components/CoPilot";

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <PaywallProvider>
            <CommandPaletteProvider>
              <CoPilotProvider>
                <div className="App min-h-screen bg-obsidian">
                  <OfflineIndicator />
                  <div className="noise-overlay" />
                  <AppRouter />
                  <PWAInstallPrompt />
                  <Toaster 
                    position="top-right"
                    toastOptions={{
                      style: {
                        background: '#0A0A0A',
                        border: '1px solid #333',
                        color: '#fff',
                      },
                    }}
                  />
                </div>
              </CoPilotProvider>
            </CommandPaletteProvider>
          </PaywallProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
